import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { Heading } from '../../../components/Heading';
import { Button } from '../../../components/Button';
import { Divider } from '../../../components/Divider';
import { Badge } from '../../../components/Badge';
import Loading from '../../../components/Loading';
import { useNotifications } from '../../../hooks/useNotifications';
import NotificationService, { type Notification } from '../../../services/notificationService';
import { UserContext } from '../../../context/userContext';
import axios from 'axios';

const NotificationViewPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { currentUser } = React.useContext(UserContext);
    const { markRead } = useNotifications();
    const [notification, setNotification] = useState<Notification | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        const numId = parseInt(id, 10);
        if (isNaN(numId)) {
            setError('Invalid notification id');
            setLoading(false);
            return;
        }
        const userId = currentUser?.id != null ? Number(currentUser.id) : null;
        const warehouseId = currentUser?.warehouse_id != null ? String(currentUser.warehouse_id) : null;
        const role = currentUser?.role ?? null;
        if (userId == null) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await NotificationService.getById(userId, warehouseId, role, numId);
                if (!cancelled) {
                    setNotification(data);
                    setError(null);
                    document.title = data?.title ?? 'Notification';
                    if (data && !data.read_at) markRead(data.id);
                }
            } catch (err) {
                if (!cancelled) {
                    if (axios.isAxiosError(err) && err.response?.status === 404) {
                        setError('Notification not found');
                        setNotification(null);
                    } else {
                        setError('Failed to load notification');
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id, currentUser?.id, currentUser?.warehouse_id]);

    if (loading) return <Loading />;
    if (error || !notification) {
        return (
            <>
                <Link to="/gp/notifications" className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 dark:text-zinc-400">
                    <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                    Back to Notifications
                </Link>
                <p className="mt-6 text-center text-zinc-600 dark:text-zinc-400">{error ?? 'Notification not found'}</p>
            </>
        );
    }

    const isTrackingOrder = notification.entity_type === 'tracking_order' && notification.link_url;

    return (
        <>
            <div className="max-lg:hidden">
                <Link to="/gp/notifications" className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 dark:text-zinc-400">
                    <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                    Back to Notifications
                </Link>
            </div>
            <div className="mt-4 lg:mt-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <Heading>{notification.title}</Heading>
                    {isTrackingOrder && (
                        <Button onClick={() => navigate(notification.link_url!)}>
                            View tracking order
                        </Button>
                    )}
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">
                        {new Date(notification.created_time).toLocaleString()}
                    </span>
                    <Badge color={notification.type === 'TRACKING_STALE' ? 'amber' : 'zinc'}>
                        {notification.type === 'TRACKING_STALE' ? 'Tracking' : notification.type}
                    </Badge>
                </div>
            </div>
            <Divider className="mt-6" />
            {notification.message && (
                <div className="mt-6">
                    <p className="text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">{notification.message}</p>
                </div>
            )}
        </>
    );
};

export default NotificationViewPage;
